import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback } from 'react';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import * as yup from 'yup';
import ContinentList from './continent-list';
import PmiTextField from 'shell/components/shared/text-field';

export interface TerritoryForm {
  code?: string;
  endonym?: string;
  exonym?: string;
  state?: string;
  continent?: string;
  electoralAgreement?: boolean;
}

const validationSchema = yup.object().shape({
  endonym: yup.string().required('Il campo endonimo è obbligatorio'),
  exonym: yup.string().required('Il campo esonimo è obbligatorio'),
  state: yup.string().required('Il campo stato governante è obbligatorio'),
  continent: yup.string().required('Il campo continente è obbligatorio'),
  code: yup.string().required('Il campo codice territorio è obbligatorio'),
  electoralAgreement: yup.boolean().required('Il campo intesa elettorale è obbligatorio'),
});

interface FormTerritoryProps {
  onOk: (form: TerritoryForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  children?: React.ReactElement;
  initialValues?: TerritoryForm;
  isEdit?: boolean;
}

const FormTerritory = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  children,
  initialValues,
  isEdit = false,
}: FormTerritoryProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formTerritory = useFormik<TerritoryForm>({
    initialValues: initialValues || {
      code: '',
      continent: '',
      state: '',
      electoralAgreement: false,
      endonym: '',
      exonym: '',
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formTerritory.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice territorio"
                  label="Codice territorio *"
                  id="code"
                  name="code"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formTerritory.values.code || ''}
                  inputProps={{
                    maxLength: '3',
                  }}
                  disabled={disabled || isEdit}
                  onChange={formTerritory.handleChange}
                  error={formTerritory.touched.code && Boolean(formTerritory.errors.code)}
                  helperText={formTerritory.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="Esonimo"
                  label="Esonimo *"
                  id="exonym"
                  name="exonym"
                  fullWidth
                  type="text"
                  variant="outlined"
                  // inputProps={{
                  //   maxLength: '24',
                  // }}
                  value={formTerritory.values.exonym || ''}
                  disabled={disabled}
                  onChange={formTerritory.handleChange}
                  error={formTerritory.touched.exonym && Boolean(formTerritory.errors.exonym)}
                  helperText={formTerritory.errors.exonym}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Endonimo"
                  label="Endonimo *"
                  id="endonym"
                  name="endonym"
                  fullWidth
                  type="text"
                  variant="outlined"
                  // inputProps={{
                  //   maxLength: '24',
                  // }}
                  value={formTerritory.values.endonym || ''}
                  disabled={disabled}
                  onChange={formTerritory.handleChange}
                  error={formTerritory.touched.endonym && Boolean(formTerritory.errors.endonym)}
                  helperText={formTerritory.errors.endonym}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="Stato Governante"
                  label="Stato Governante *"
                  id="state"
                  name="state"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '3',
                  }}
                  value={formTerritory.values.state || ''}
                  disabled={disabled}
                  onChange={formTerritory.handleChange}
                  error={formTerritory.touched.state && Boolean(formTerritory.errors.state)}
                  helperText={formTerritory.errors.state}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <ContinentList
                  value={formTerritory.values.continent || ''}
                  onChange={formTerritory.handleChange}
                  error={formTerritory.touched.continent && Boolean(formTerritory.errors.continent)}
                  disabled={disabled}
                  helperText={formTerritory.errors.continent}
                />
              </Grid>
              {userProfile.datiLogin?.profile?.feature?.elettorale === '4' ? (
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="electoralAgreement"
                        name="electoralAgreement"
                        checked={formTerritory.values.electoralAgreement || false}
                        onChange={() =>
                          formTerritory.setFieldValue(
                            'electoralAgreement',
                            !formTerritory.values.electoralAgreement
                          )
                        }
                        disabled={disabled}
                        inputProps={{ 'aria-label': 'Intesa Elettorale' }}
                      />
                    }
                    label="Intesa Elettorale"
                  />
                </Grid>
              ) : undefined}
            </Grid>
            <Grid item xs={12}>
              {children}
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormTerritory.displayName = 'Form Territory component';

export default FormTerritory;
